import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";

// Prism-based highlighter (fast + tree-shakable)
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
// Centralized theme configuration
import { PRISM_STYLES, LIGHT_THEME } from "../../config/SyntaxThemes";

// Languages (register only what we need)
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import js from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql";
import md from "react-syntax-highlighter/dist/esm/languages/prism/markdown";

// Register languages once (module scope)
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("javascript", js);
SyntaxHighlighter.registerLanguage("js", js);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("sql", sql);
SyntaxHighlighter.registerLanguage("markdown", md);


/**
 * EditorPreview
 *
 * Props:
 * - title?: string
 * - subtitle?: string
 * - technology?: string
 * - skill?: string
 * - body?: string (markdown)
 * - publish?: boolean
 * - theme?: "light" | "dark" (code theme; default "light")
 * - className?: string (extra class for outer wrapper)
 * - heroImage?: string (optional, in case you re-introduce images later)
 */
function EditorPreview({
  title = "",
  subtitle = "",
  technology = "",
  skill = "",
  body = "",
  publish = false,
  theme = "light",
  className = "",
  heroImage = "",
}) {
  // Markdown plugins (math + gfm; raw allowed to support inline HTML you may already rely on)
  const mdPlugins = useMemo(() => [remarkGfm, remarkMath], []);
  const rehypePlugins = useMemo(() => [rehypeRaw, [rehypeKatex, { strict: false }]], []);

  // Inline code component (for single backticks)
  const InlineCode = ({ className, children, ...props }) => (
    <code
      className={className}
      {...props}
      style={{
        background: "var(--code-bg)",
        color: "var(--code-text)",
        borderRadius: 6,
        padding: "0.1rem 0.35rem",
        fontSize: "0.95em",
      }}
    >
      {children}
    </code>
  );

  // Pre block component (for fenced code blocks)
  const PreBlock = ({ children }) => {
    const codeEl = Array.isArray(children) ? children[0] : children;
    const className = codeEl?.props?.className || "";
    const match = /language-(\w+)/.exec(className);
    const language = match?.[1]?.toLowerCase();
    const code = String(codeEl?.props?.children ?? "").replace(/\n$/, "");

    return (
      <SyntaxHighlighter
        style={PRISM_STYLES[theme] || LIGHT_THEME}
        language={language || undefined}
        PreTag="div"
        wrapLongLines
        customStyle={{ margin: 0, borderRadius: 8, fontSize: "0.95rem" }}
      >
        {code}
      </SyntaxHighlighter>
    );
  };

  // Table wrapper component (matches ProjectDetail styling)
  const TableWrap = ({ children, ...props }) => (
    <div className="table-scroll">
      <table {...props}>{children}</table>
    </div>
  );

  return (
    <div className={`pc-pane pc-preview ${className}`.trim()}>
      {/* Optional hero image (kept for parity if you re-add it later) */}
      {heroImage?.trim() && (
        <div className="pc-hero">
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img src={heroImage} alt="Header image" />
        </div>
      )}

      {/* Header — matches ProjectDetail layout */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ padding: "2% 0" }}>
          {title?.trim() && <h1 style={{ fontWeight: 800, marginBottom: 6 }}>{title}</h1>}
          {subtitle?.trim() && <h3 style={{ marginTop: 0, fontSize: "0.9em" }}>{subtitle}</h3>}
          {(technology?.trim() || skill?.trim()) && (
            <p style={{ color: "#59c6c1", marginTop: 12, fontSize: "1em" }}>
              <strong>
                {technology?.trim() || ""}
                {technology?.trim() && skill?.trim() && (
                  <span style={{ color: "lightgray" }}> | </span>
                )}
                {skill?.trim() || ""}
              </strong>
            </p>
          )}
        </div>
      </div>
      <hr />

      {/* Markdown body */}
      <div className="pc-markdown">
        <ReactMarkdown
          className="custom-body-markdown"
          remarkPlugins={mdPlugins}
          rehypePlugins={rehypePlugins}
          components={{
            pre: PreBlock,
            code: InlineCode,
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
            img: ({ node, ...props }) => (
              <img {...props} loading="lazy" style={{ maxWidth: "100%", height: "auto" }} />
            ),
            table: TableWrap,
          }}
        >
          {body?.trim() ? body : "*Start writing in the editor to see a live preview.*"}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default EditorPreview;
